import { useCallback, useEffect, useMemo, useState } from "react"
import { ChevronRight, Filter, Loader2, Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { SectionCard } from "@/components/ui/section-card"
import { Table, TableColumn } from "@/components/ui/table"
import { cn } from "@/lib/class-control"
import { Payment } from "@/types/payment"
import { TabelaLancamentosProps } from "../relacao-pagamentos.types"
import { StatusBadge } from "./status-badge"
import { AcoesLinha } from "./acoes-linha"
import { DialogoLancamento } from "./dialog-relacao-pagamentos/dialogo-lancamento"
import InputSearch from "@/components/ui/input-search"
import ButtonFilter from "@/components/ui/button-filter"

const PAGE_SIZE = 10

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)

const TABLE_COLUMNS: TableColumn[] = [
  { key: "n", label: "Nº" },
  { key: "creditor", label: "Credor" },
  { key: "paymentDate", label: "Data do Pagamento" },
  { key: "fiscalDocType", label: "Tipo Doc. Fiscal", headerClassName: "min-w-[100px]" },
  { key: "fiscalDocNumber", label: "Nº Doc. Fiscal", headerClassName: "min-w-[100px]" },
  { key: "issueDate", label: "Data de Emissão" },
  { key: "amount", label: "Valor" },
  { key: "status", label: "Status" },
  { key: "actions", label: "Ações" },
]

function CreditorAvatar({ payment }: { payment: Payment }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={cn(
          "w-8 h-8 rounded-full bg-gray-600 flex-shrink-0",
          "flex items-center justify-center text-white text-[10px]"
        )}
      >
        {payment.creditorAvatar ? (
          <img
            src={payment.creditorAvatar}
            alt={payment.creditorName}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          getInitials(payment.creditorName)
        )}
      </div>
      <span className="text-white text-xs font-semibold truncate">
        {payment.creditorName}
      </span>
    </div>
  )
}

function MobilePaymentCard({
  payment,
  onView,
  onEdit,
  onDelete,
}: {
  payment: Payment
  onView: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const paymentDate =
    typeof payment.paymentDate === "string"
      ? payment.paymentDate
      : payment.paymentDate.toLocaleDateString("pt-BR")

  const emissionDate =
    typeof payment.emissionDate === "string"
      ? payment.emissionDate
      : payment.emissionDate.toLocaleDateString("pt-BR")

  return (
    <div className="rounded-lg border border-gray-800 p-3">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "w-9 h-9 rounded-full bg-gray-600",
              "flex items-center justify-center text-white text-xs"
            )}
          >
            {payment.creditorAvatar ? (
              <img
                src={payment.creditorAvatar}
                alt={payment.creditorName}
                className="w-9 h-9 rounded-full object-cover"
              />
            ) : (
              getInitials(payment.creditorName)
            )}
          </div>
          <div>
            <p className="text-white text-xs font-medium">{payment.creditorName}</p>
            <p className="text-gray-400 text-[11px]">
              Nº {payment.number} • {payment.fiscalDocType}
            </p>
          </div>
        </div>
        <StatusBadge status={payment.status} />
      </div>

      <div className="grid grid-cols-2 gap-2 text-[11px] text-gray-300 mt-3">
        <div>
          <span className="text-gray-400 block">Pagamento</span>
          {paymentDate}
        </div>
        <div>
          <span className="text-gray-400 block">Emissão</span>
          {emissionDate}
        </div>
        <div>
          <span className="text-gray-400 block">Nº Doc.</span>
          {payment.fiscalDocNumber}
        </div>
        <div>
          <span className="text-gray-400 block">Valor</span>
          {payment.amount}
        </div>
      </div>

      <div className="mt-3 flex justify-end">
        <AcoesLinha onView={onView} onEdit={onEdit} onDelete={onDelete} />
      </div>
    </div>
  )
}

export function TabelaLancamentos({
  data,
  onView,
  onEdit,
  onDelete,
  onSave,
  navigate,
  isLoading,
}: TabelaLancamentosProps) {
  const [searchTerm, setSearchTerm] = useState("")
  const [page, setPage] = useState(1)
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null)
  const [dialogMode, setDialogMode] = useState<"view" | "edit">("view")
  const [dialogOpen, setDialogOpen] = useState(false)

  const openDialog = (payment: Payment, mode: "view" | "edit") => {
    setSelectedPayment(payment)
    setDialogMode(mode)
    setDialogOpen(true)
  }

  const closeDialog = () => {
    setDialogOpen(false)
    setSelectedPayment(null)
  }

  const handleView = (payment: Payment) => {
    openDialog(payment, "view")
    onView(payment)
  }

  const handleEdit = (payment: Payment) => {
    openDialog(payment, "edit")
    onEdit(payment)
  }

  const handleSave = (payment: Payment) => {
    onSave?.(payment)
    closeDialog()
  }

  const handleDeleteFromDialog = (payment: Payment) => {
    onDelete(payment)
  }

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase().trim()
    if (!term) return data
    return data.filter(
      (p) =>
        p.creditorName.toLowerCase().includes(term) ||
        p.number.toLowerCase().includes(term) ||
        p.fiscalDocNumber.toLowerCase().includes(term)
    )
  }, [data, searchTerm])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))

  const changePage = useCallback(
    (delta: number) => setPage((p) => Math.min(totalPages, Math.max(1, p + delta))),
    [totalPages]
  )

  useEffect(() => {
    setPage(1)
  }, [searchTerm])

  const currentPage = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE
    return filtered.slice(start, start + PAGE_SIZE)
  }, [filtered, page])

  const tableRows = currentPage.map((p) => ({
    n: p.number,
    creditor: <CreditorAvatar payment={p} />,
    paymentDate:
      typeof p.paymentDate === "string"
        ? p.paymentDate
        : p.paymentDate.toLocaleDateString("pt-BR"),
    fiscalDocType: p.fiscalDocType,
    fiscalDocNumber: p.fiscalDocNumber,
    issueDate:
      typeof p.emissionDate === "string"
        ? p.emissionDate
        : p.emissionDate.toLocaleDateString("pt-BR"),
    amount: p.amount,
    status: <StatusBadge status={p.status} />,
    actions: (
      <AcoesLinha
        onView={() => handleView(p)}
        onEdit={() => handleEdit(p)}
        onDelete={() => onDelete(p)}
      />
    ),
  }))

  return (
    <>
      <DialogoLancamento
        payment={selectedPayment}
        open={dialogOpen}
        onClose={closeDialog}
        onSave={handleSave}
        onDelete={handleDeleteFromDialog}
        mode={dialogMode}
      />

      <SectionCard className="p-3 md:p-4">
        {/* Cabeçalho e controles */}
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-5">
          <h3 className="text-white text-lg font-bold flex-1">Lançamentos</h3>

          <div className="flex items-center gap-2">
            <InputSearch
              focus="none"
              positionIcon="end"
              value={searchTerm}
              onChange={setSearchTerm}
            />

            <ButtonFilter
              positionIcon="end"
              className="w-24 justify-around text-xs text-white"
            />

            <Button
              size="sm"
              className="w-[180px] h-btn-default bg-[#7846ff] hover:bg-[#7846ff]/90 text-[#f5f2ff] gap-1 px-btn-x py-btn-y rounded-lg font-bold text-xs"
              onClick={() => navigate("/financeiro/relacao-pagamentos/novo")}
            >
              <Plus className="w-4 h-4" />
              Novo Lançamento
            </Button>
          </div>
        </div>

        {/* Tabela (desktop) */}
        <div className="hidden md:block">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#7846ff] animate-spin" />
              <span className="ml-3 text-gray-400 text-sm">
                Carregando lançamentos...
              </span>
            </div>
          ) : currentPage.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <p className="text-sm">Nenhum lançamento encontrado</p>
            </div>
          ) : (
            <Table columns={TABLE_COLUMNS} data={tableRows} />
          )}
        </div>

        {/* Lista (mobile) */}
        {isLoading ? (
          <div className="md:hidden flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#7846ff] animate-spin" />
            <span className="ml-3 text-gray-400 text-sm">Carregando...</span>
          </div>
        ) : (
          <div className="md:hidden space-y-3">
            {currentPage.map((p) => (
              <MobilePaymentCard
                key={p.id}
                payment={p}
                onView={() => handleView(p)}
                onEdit={() => handleEdit(p)}
                onDelete={() => onDelete(p)}
              />
            ))}
          </div>
        )}

        {/* Paginação */}
        <div className="mt-6 flex items-center justify-between gap-2">
          <p className="text-[#888888] text-xs font-normal">
            Exibindo {currentPage.length} de {filtered.length} transações
          </p>

          <div className="flex items-center gap-[13px]">
            <div
              className={cn(
                "w-[40px] h-[40px] flex items-center justify-center rounded-[20px] rotate-180",
                page === 1 ? "cursor-default opacity-50" : "cursor-pointer"
              )}
              onClick={() => page !== 1 && changePage(-1)}
            >
              <ChevronRight className="w-[18px] h-[9px]" />
            </div>

            {Array.from({ length: Math.min(totalPages, 4) }, (_, i) => i + 1).map(
              (i) => (
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  className={cn(
                    "w-[30px] h-[30px] rounded-[20px] text-xs font-normal flex items-center justify-center",
                    page === i
                      ? "bg-[#7846ff] text-white"
                      : "text-white hover:bg-white/10"
                  )}
                >
                  {i}
                </button>
              )
            )}

            <div
              className={cn(
                "w-[40px] h-[40px] flex items-center justify-center rounded-[20px]",
                page === totalPages ? "cursor-default opacity-50" : "cursor-pointer"
              )}
              onClick={() => page !== totalPages && changePage(1)}
            >
              <ChevronRight className="w-[18px] h-[9px]" />
            </div>
          </div>
        </div>
      </SectionCard>
    </>
  )
}
